// Step 5 (Social Post route only) — pick one of the designed templates.
// Replaces StepBackground for the social-post flow. The picked design
// determines which React component renders in Step 6 (Final).
//
// Phase 1: only Editorial Cream is implemented. The other three are
// shown as "Coming soon" placeholders so the gallery still reads as a
// curated set.
//
// Layout follows the Fashion Model picker (StepFashionModel.tsx) but at
// 20% larger card size — one fewer column at every breakpoint, square
// cards since social posts are 1:1 (1080×1080). Full-bleed preview
// image with name + vibe overlaid on a bottom gradient.

import { memo, useCallback, useEffect, useRef, useState } from 'react';
import StepHeader from './StepHeader';
import { JSON_TEMPLATES } from './social-designs/json-templates/registry';
import JsonSocialPost from './social-designs/json-templates/JsonSocialPost';
import { SAMPLE_SLOTS } from './social-designs/json-templates/sample-slots';

/** Design id used everywhere downstream. The 8 hand-coded React templates
 *  (editorial, blush, botanical, mono, noir, pop, vintage, street) have
 *  reserved literal ids; JSON-defined templates contribute their own
 *  ids dynamically via the registry. We widen the type to `string` so
 *  the runtime registry can supply new ids without a code change,
 *  losing a bit of autocomplete in exchange for the extensibility. */
export type SocialDesignId = string;

/** The original 8 literal ids — kept as a const for documentation /
 *  defensive runtime checks. The dispatcher in StepSocialFinal checks
 *  the JSON registry FIRST and falls through to a switch on these. */
export const LEGACY_SOCIAL_DESIGN_IDS = [
  'editorial', 'blush', 'botanical', 'mono',
  'noir', 'pop', 'vintage', 'street',
] as const;

interface DesignOption {
  id: SocialDesignId;
  label: string;
  description: string;
  vibe: string;           // short style descriptor for the card
  preview: string;        // path to a preview thumbnail (PNG/SVG/etc.)
  available: boolean;     // false = "Coming soon" placeholder
  /** When set, the picker card renders a LIVE preview of the JSON
   *  template at small scale (using JsonSocialPost + SAMPLE_SLOTS)
   *  instead of looking for a static PNG. Set for JSON-registry
   *  entries; undefined for legacy hand-coded templates. */
  jsonId?: string;
}

// Legacy hand-coded React-component designs. HIDDEN from the picker for
// now (May 29) — the JSON templates are the active set. Nothing is deleted:
// the render components (PostEditorial, PostBlush, …) and the legacy dispatch
// in StepSocialFinal stay intact, so re-enabling is a one-line change —
// spread `...LEGACY_DESIGNS` back into DESIGNS below.
const LEGACY_DESIGNS: DesignOption[] = [
  {
    id: 'editorial',
    label: 'Editorial Cream',
    description: 'A two-column editorial layout with serif italic headline, bullet proof points, and a circular CTA. Premium magazine feel.',
    vibe: 'Editorial · Cream · Serif',
    preview: '/social-templates/previews/editorial.png',
    available: true,
  },
  {
    id: 'blush',
    label: 'Soft Blush',
    description: 'A warm modern layout with rounded product blob, check-marked feature list, and a soft pink palette.',
    vibe: 'Modern · Blush · Friendly',
    preview: '/social-templates/previews/blush.png',
    available: true,
  },
  {
    id: 'botanical',
    label: 'Botanical Sage',
    description: 'Numbered-list editorial with sage greens, brand mark, and roman-numeral feature points.',
    vibe: 'Botanical · Sage · Classical',
    preview: '/social-templates/previews/botanical.png',
    available: true,
  },
  {
    id: 'mono',
    label: 'Mono Editorial',
    description: 'High-contrast mono editorial with a season tag, numbered features, and a typographic hero.',
    vibe: 'Mono · High contrast · Bold',
    preview: '/social-templates/previews/mono.png',
    available: true,
  },
  {
    id: 'noir',
    label: 'Editorial Noir',
    description: 'Bodoni-driven editorial with a masthead, issue meta, roman-numeral specs, and a refined product caption.',
    vibe: 'Editorial · Noir · Serif',
    preview: '/social-templates/previews/noir.png',
    available: true,
  },
  {
    id: 'pop',
    label: 'Pop Energy',
    description: 'High-saturation yellow + pink layout with a chunky highlighted headline, blob product frame, and a circular newdrop badge.',
    vibe: 'Pop · Bold · Saturated',
    preview: '/social-templates/previews/pop.png',
    available: true,
  },
  {
    id: 'vintage',
    label: 'Boutique Vintage',
    description: 'Warm cream layout with a wax-seal monogram, Playfair italic headline, fleuron divider, and corner-bracketed product card.',
    vibe: 'Vintage · Boutique · Ornate',
    preview: '/social-templates/previews/vintage.png',
    available: true,
  },
  {
    id: 'street',
    label: 'Streetwear Raw',
    description: 'Dark grid canvas with orange tape strips, monospace meta codes, Anton display headline, and a barcoded product slot.',
    vibe: 'Streetwear · Raw · Technical',
    preview: '/social-templates/previews/street.png',
    available: true,
  },
];

// The picker's active list. Legacy designs are intentionally NOT spread in
// (hidden for now) — to show them again, add `...LEGACY_DESIGNS,` as the
// first entry below.
const DESIGNS: DesignOption[] = [
  // ...LEGACY_DESIGNS,
  // JSON-defined templates auto-load below from the registry, sorted by
  // the trailing number in their id (so test-template-2 sorts before
  // test-template-10 — a plain string sort wouldn't). Ids without a
  // trailing number (e.g. legacy "untitled-*") sort to the end.
  ...Object.values(JSON_TEMPLATES)
    .slice()
    .sort((a, b) => {
      const numOf = (id: string) => {
        const m = id.match(/(\d+)\s*$/);
        return m ? parseInt(m[1], 10) : Number.MAX_SAFE_INTEGER;
      };
      const na = numOf(a.id);
      const nb = numOf(b.id);
      return na !== nb ? na - nb : a.id.localeCompare(b.id);
    })
    .map((tpl): DesignOption => ({
      id: tpl.id,
      label: tpl.name,
      description: `JSON template — ${tpl.elements.length} elements on a ${tpl.canvas.width}×${tpl.canvas.height} canvas.`,
      vibe: 'JSON template',
      // No static PNG needed — DesignCard renders a live JsonSocialPost
      // preview when `jsonId` is set. preview is kept as a noop string
      // so the type stays simple.
      preview: '',
      available: true,
      jsonId: tpl.id,
    })),
];

/** Live miniaturized preview of a JSON template inside a picker card.
 *  Renders the template at its native 1080×1080 and CSS-transforms it
 *  down to the measured card width. ResizeObserver keeps the scale in
 *  sync across breakpoints so the design always FILLS the card —
 *  hardcoded 0.2 (the previous approach) looked tiny on wide cards
 *  and clipped on narrow ones. */
const JsonTemplatePreview = memo(function JsonTemplatePreview({
  jsonTemplate,
  brandLogoUrl,
}: {
  jsonTemplate: import('./social-designs/json-templates/types').JsonTemplate
  brandLogoUrl?: string | null
}) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.2);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const update = () => {
      const w = el.clientWidth;
      if (w > 0) setScale(w / jsonTemplate.canvas.width);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [jsonTemplate.canvas.width]);

  return (
    <div
      ref={wrapperRef}
      className="absolute inset-0 overflow-hidden bg-white pointer-events-none"
    >
      <div
        style={{
          width: jsonTemplate.canvas.width,
          height: jsonTemplate.canvas.height,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        <JsonSocialPost config={jsonTemplate} slots={SAMPLE_SLOTS} productImageUrl="" brandLogoUrl={brandLogoUrl} />
      </div>
    </div>
  );
});

interface DesignCardProps {
  design: DesignOption;
  isActive: boolean;
  onSelect: (id: SocialDesignId) => void;
  brandLogoUrl?: string | null;
}

// Memoized card — re-renders only when its own props change. Matches
// StepFashionModel's card pattern (full-bleed image, bottom overlay) so
// the two pickers feel like one family.
const DesignCard = memo(function DesignCard({ design, isActive, onSelect, brandLogoUrl }: DesignCardProps) {
  const handleClick = useCallback(() => {
    if (design.available) onSelect(design.id);
  }, [design.id, design.available, onSelect]);

  const interactive = design.available;
  const borderClass = !interactive
    ? 'border-surface-dark/30 opacity-70 cursor-not-allowed'
    : isActive
      ? 'border-accent shadow-lg shadow-accent/15'
      : 'border-transparent hover:border-surface-dark/50 bg-surface hover:shadow-sm';

  // For JSON templates, render a live miniaturized preview by
  // scaling JsonSocialPost down to fit the card. Legacy templates use
  // their pre-authored PNG thumbnails (the original code path).
  const jsonTemplate = design.jsonId ? JSON_TEMPLATES[design.jsonId] : null;

  return (
    <button
      onClick={handleClick}
      disabled={!interactive}
      className={`group relative rounded-xl overflow-hidden border-2 text-left aspect-square w-full active:scale-[0.98] ${borderClass}`}
    >
      {jsonTemplate ? (
        // ----- LIVE preview for JSON-defined templates -----
        // The template renders at its native 1080×1080 inside a wrapper
        // that's CSS-transformed down to fit the actual card width via
        // a ResizeObserver. Pointer-events disabled so clicks bubble
        // up to the button.
        <JsonTemplatePreview jsonTemplate={jsonTemplate} brandLogoUrl={brandLogoUrl} />
      ) : (
        <>
          {/* Full-bleed preview image. `object-cover` so the whole card is
              filled; falls back to a gradient + label if the PNG is missing. */}
          <img
            src={design.preview}
            alt={`${design.label} preview`}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
          {/* Fallback background — shown when the <img> fails. Lives BELOW
              the image in z-order so it's only visible when the image is
              hidden by the onError handler. */}
          <div className="absolute inset-0 -z-10 bg-gradient-to-br from-surface to-surface-dark/30 flex flex-col items-center justify-center text-center p-4">
            <i className="ri-image-line text-dark-muted text-3xl mb-1" />
            <p className="text-xs text-dark-muted font-semibold uppercase tracking-wider">
              Preview missing
            </p>
          </div>
        </>
      )}

      {/* Bottom gradient + text overlay — same treatment as
          StepFashionModel cards. */}
      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-dark/80 to-transparent pointer-events-none" />
      <div className="absolute bottom-2 left-2 right-2">
        <p className="text-xs font-bold text-white leading-tight">{design.label}</p>
        <p className="text-[11px] text-white/70 leading-tight">{design.vibe}</p>
      </div>

      {/* Active check */}
      {isActive && interactive && (
        <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-accent flex items-center justify-center">
          <i className="ri-check-line text-white text-xs" />
        </div>
      )}

      {/* Coming-soon badge */}
      {!interactive && (
        <div className="absolute top-2 right-2 px-2 py-0.5 rounded-md bg-amber-100 text-amber-700 text-xs font-bold uppercase tracking-wide">
          Coming soon
        </div>
      )}
    </button>
  );
});

interface StepSocialDesignProps {
  selected: SocialDesignId | null;
  onSelect: (id: SocialDesignId) => void;
  /** /appv2's Section wrapper supplies its own header; drop h-full +
   *  internal scroll so the page handles scrolling instead. */
  compact?: boolean;
  /** Brand logo URL — rendered in any template's `logo` slot so the
   *  picker thumbnails show the user's brand on every design. */
  brandLogoUrl?: string | null;
}

export default function StepSocialDesign({ selected, onSelect, compact, brandLogoUrl }: StepSocialDesignProps) {
  const selectedDesign = DESIGNS.find((d) => d.id === selected);

  return (
    <div className={compact ? '' : 'h-full flex flex-col'}>
      {!compact && (
        <StepHeader
          icon="ri-layout-grid-line"
          title="Choose a Design"
          subtitle="Pick a designed template. AI fills the headline, subhead, bullets, and CTA based on your product. You can edit any field on the next step."
        />
      )}

      {/* One column fewer than the Fashion Model grid at every breakpoint —
          gives ~20% larger cards while reading as the same picker family.
          Square cards since social posts render at 1:1. */}
      <div className={compact ? '' : 'flex-1 min-h-0 overflow-y-auto'}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {DESIGNS.map((design) => (
            <DesignCard
              key={design.id}
              design={design}
              isActive={selected === design.id}
              onSelect={onSelect}
              brandLogoUrl={brandLogoUrl}
            />
          ))}
        </div>
      </div>

      {/* Selected pill — shows the chosen design's description so the long
          copy that used to live inside the card is still discoverable.
          Matches StepFashionModel's selected-pill placement. */}
      {selectedDesign && (
        <div className="flex-shrink-0 flex items-center justify-center gap-2 pt-3">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-light border border-primary/20 max-w-full">
            <i className="ri-check-line text-primary text-xs flex-shrink-0" />
            <span className="text-xs font-semibold text-primary flex-shrink-0">{selectedDesign.label}</span>
            <span className="text-xs text-dark-muted truncate">{selectedDesign.description}</span>
          </div>
        </div>
      )}
    </div>
  );
}
